/**
 * Snapping for the assembler's move gesture. A room dragged near another
 * room's parallel wall snaps so the two walls coincide exactly. Walls that
 * face each other have one snap: the shared wall, with their inner faces one
 * thickness apart; walls facing the same way run flush, inner face on inner
 * face. Once a wall pair coincides, the closest pair of opening edges on those
 * two walls (the start or end of any door, window, or opening), or the closest
 * perpendicular wall pair, snaps the room along the wall as well. The reach
 * is the editors' shared `snapReach`: a few screen pixels, up to a foot.
 */
import { type FloorPlanRoom, type Point, WALL_THICKNESS } from '@kaiizen/kai-floor-plan';
import { acrossOn, add, alongOn, dot, overlapOf, parallel, perpendicular, pointAt, roomSnapLines, type SnapLine } from './snapLines';

const shifted = (lines: readonly SnapLine[], delta: Point): SnapLine[] => lines.map(line => ({ ...line, origin: add(line.origin, delta) }));

interface Correction {
  delta: Point;
  distance: number;
}

interface WallSnap extends Correction {
  line: SnapLine;
  partner: SnapLine;
}

/**
 * Whether two faces may coincide: walls facing each other share a wall (my
 * outer face on their inner, or the reverse: the one snap for such walls,
 * their inner faces one thickness apart); walls facing the same way run
 * flush (inner on inner).
 */
const mayCoincide = (line: SnapLine, partner: SnapLine): boolean =>
  dot(line.perp, partner.perp) < 0 ? line.face !== partner.face : line.face === 'inner' && partner.face === 'inner';

/**
 * The closest pair of parallel faces within reach that may coincide, one of
 * `mine` and one of `theirs`, whose extents meet along the wall (overlapping,
 * or apart by no more than a shared wall's thickness and the reach), as the
 * correction that brings my face onto theirs. `across` limits my faces to
 * those perpendicular to a first snap's wall.
 */
function closestWallSnap(mine: readonly SnapLine[], theirs: readonly SnapLine[], reach: number, across?: SnapLine): WallSnap | null {
  const gapAllowed = WALL_THICKNESS + reach;
  let best: WallSnap | null = null;
  for (const line of mine) {
    if (across && !perpendicular(line, across)) continue;
    for (const partner of theirs) {
      if (!parallel(line, partner) || !mayCoincide(line, partner)) continue;
      if (overlapOf(line, partner) < -gapAllowed) continue;
      const offset = acrossOn(line, partner.origin);
      const distance = Math.abs(offset);
      if (distance > reach || (best && distance >= best.distance)) continue;
      best = { delta: { x: line.perp.x * offset, y: line.perp.y * offset }, distance, line, partner };
    }
  }
  return best;
}

/** The closest pair of opening edges on two coincident faces, as the correction along the wall that lines them up. */
function closestOpeningSnap(line: SnapLine, partner: SnapLine, reach: number): Correction | null {
  let best: Correction | null = null;
  for (const mine of line.openings) {
    for (const theirs of partner.openings) {
      const offset = alongOn(line, pointAt(partner, theirs)) - mine;
      const distance = Math.abs(offset);
      if (distance > reach || (best && distance >= best.distance)) continue;
      best = { delta: { x: line.dir.x * offset, y: line.dir.y * offset }, distance };
    }
  }
  return best;
}

/**
 * The correction that snaps `dragged`, at its candidate placement, to
 * `others`: onto the closest wall within reach, then along that wall onto
 * the closest pair of opening edges or perpendicular wall pair within reach.
 * Zero when no wall of theirs is within reach. A zero correction is a plain
 * zero, never −0.
 */
export function snapRoom(dragged: FloorPlanRoom, others: readonly FloorPlanRoom[], reach: number): Point {
  const none = { x: 0, y: 0 };
  const theirs = others.flatMap(roomSnapLines);
  if (!theirs.length) return none;
  const mine = roomSnapLines(dragged);
  const first = closestWallSnap(mine, theirs, reach);
  if (!first) return none;
  const line = { ...first.line, origin: add(first.line.origin, first.delta) };
  const second = [closestOpeningSnap(line, first.partner, reach), closestWallSnap(shifted(mine, first.delta), theirs, reach, first.line)]
    .filter((candidate): candidate is Correction => candidate !== null)
    .sort((a, b) => a.distance - b.distance)[0];
  const delta = second ? add(first.delta, second.delta) : first.delta;
  return { x: delta.x || 0, y: delta.y || 0 };
}
